import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, MessageCircle } from "lucide-react";
import {
  TREATMENT_OPTIONS, PREFERRED_TIME_OPTIONS, CONSENT_TEXT, PRIVACY_LINE,
  CLINIC_TIMINGS, PHONE_PRIMARY, PHONE_PRIMARY_TEL, getWhatsAppUrl, WHATSAPP_BOOKING_INTRO,
} from "@/lib/constants";
import { trackCall, trackWhatsApp, trackFormSubmit } from "@/lib/analytics";

interface Props {
  /** Page/treatment name, used for the heading, CTA label and lead source. */
  treatment: string;
  /** Pre-selected value from TREATMENT_OPTIONS, when it maps cleanly. */
  defaultOption?: string;
  ctaLabel?: string;
  sourcePage?: string;
}

const InlineBookingForm = ({ treatment, defaultOption, ctaLabel, sourcePage }: Props) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: defaultOption && TREATMENT_OPTIONS.includes(defaultOption) ? defaultOption : "",
    time: "",
    message: "",
    consent: false,
    honeypot: "",
  });
  const [error, setError] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.honeypot) return;
    if (!form.name.trim()) return setError("Please enter your name");
    if (!/^[6-9]\d{9}$/.test(form.phone)) return setError("Enter a valid 10-digit mobile number");
    if (!form.service) return setError("Please select the treatment you need");
    if (!form.consent) return setError("Please tick the consent box so we can contact you");
    setError("");
    trackFormSubmit(sourcePage || "service_page", form.service);
    const msg = `${WHATSAPP_BOOKING_INTRO} ${form.service}.\nName: ${form.name}\nMobile: ${form.phone}${form.time ? `\nPreferred time: ${form.time}` : ""}${form.message ? `\nMessage: ${form.message}` : ""}\n(Source: ${treatment} page)`;
    window.open(getWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
    navigate("/thank-you");
  };

  return (
    <section id="book" className="py-10 md:py-20 bg-muted/40 border-y border-border scroll-mt-20">
      <div className="container max-w-3xl">
        <div className="text-center mb-8">
          <span className="text-eyebrow text-primary">Book Your Visit</span>
          <h2 className="font-heading text-2xl md:text-3xl font-light mt-2">
            {ctaLabel || `Book ${treatment} Consultation`}
          </h2>
          <p className="text-muted-foreground text-sm mt-3">
            Share your name and mobile number — our team will call or WhatsApp you to confirm a slot.
          </p>
        </div>

        <form onSubmit={submit} className="bg-card border border-border rounded-lg p-4 md:p-8 space-y-4 min-w-0" noValidate>
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            value={form.honeypot}
            onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              placeholder="Full Name"
              autoComplete="name"
              aria-label="Full name"
              maxLength={100}
              className="h-12 text-base"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              placeholder="Mobile Number (10 digits)"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              aria-label="Mobile number"
              maxLength={10}
              className="h-12 text-base"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger className="h-auto min-h-12 text-base min-w-0 text-left [&>span]:min-w-0 [&>span]:whitespace-normal" aria-label="Treatment needed"><SelectValue placeholder="Treatment Needed" /></SelectTrigger>
              <SelectContent>{TREATMENT_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
              <SelectTrigger className="h-12 text-base min-w-0" aria-label="Preferred time"><SelectValue placeholder="Preferred Time (optional)" /></SelectTrigger>
              <SelectContent>{PREFERRED_TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder="Tell us about your concern (optional)"
            aria-label="Tell us about your concern"
            rows={3}
            className="text-base"
            maxLength={500}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <label className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]"
            />
            <span>{CONSENT_TEXT}</span>
          </label>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <Button type="submit" size="lg" className="w-full h-auto min-h-12 py-3 px-3 whitespace-normal tracking-normal">
            {ctaLabel || `Book ${treatment} Consultation`}
          </Button>
          <p className="text-xs text-muted-foreground text-center">{PRIVACY_LINE}</p>
          <p className="text-xs text-muted-foreground text-center">
            Mon–Sat: {CLINIC_TIMINGS.weekdays} · Sunday: {CLINIC_TIMINGS.sunday}
          </p>

          <div className="flex flex-wrap gap-3 justify-center pt-2 border-t border-border">
            <Button asChild variant="outline" className="mt-4 max-w-full px-3 tracking-normal">
              <a href={PHONE_PRIMARY_TEL} onClick={() => trackCall(`${sourcePage || "service"}_form`)}>
                <Phone className="h-4 w-4 mr-2" /> Call {PHONE_PRIMARY}
              </a>
            </Button>
            <Button asChild variant="outline" className="mt-4 border-whatsapp text-whatsapp hover:bg-whatsapp hover:text-whatsapp-foreground tracking-normal">
              <a
                href={getWhatsAppUrl(`${WHATSAPP_BOOKING_INTRO} ${treatment}.`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsApp(`${sourcePage || "service"}_form`)}
              >
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Us
              </a>
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default InlineBookingForm;
